import axios, {AxiosResponse} from "axios";
import {WorklogsEntry} from "@/model/Worklogs";
import {SpringPagedResult} from "@/service/PagedResult";
import {WorklogType} from "@/enums";

const PATH = "/v2/worklogs";
// The largest page size the endpoint accepts
const PAGE_SIZE = 100;

/**
 * The body of POST /v2/worklogs.
 */
export interface WorklogRequest {
  // The employee's UUID
  employeeId: string;
  // The order's UUID
  orderId: string;
  date: string;
  // HH:mm
  start: string;
  // HH:mm, not before start; equal to start for an open worklog
  end: string;
  type: WorklogType;
  comment: string | null;
}

/**
 * The body of PUT /v2/worklogs/{uuid}. The employee, order and day of a worklog can not be changed.
 */
export interface WorklogUpdateRequest {
  // HH:mm
  start: string;
  // HH:mm, not before start
  end: string;
  type: WorklogType;
  // Omitted or null removes the comment
  comment: string | null;
}

class WorklogServiceV2 {

  /**
   * Creates a worklog for any employee on any order. Only a caller with the authority to create worklogs may do so.
   */
  create(request: WorklogRequest): Promise<AxiosResponse<WorklogsEntry>> {
    return axios.post(PATH, request);
  }

  /**
   * Changes the times, type and comment of a worklog by its uuid. The worklog is replaced by a new one, which the
   * response returns with a new uuid.
   */
  update(uuid: string, request: WorklogUpdateRequest): Promise<AxiosResponse<WorklogsEntry>> {
    return axios.put(`${PATH}/${uuid}`, request);
  }

  /**
   * Deletes a worklog by its uuid. Only a caller with the authority to delete worklogs may do so.
   */
  delete(uuid: string): Promise<AxiosResponse<void>> {
    return axios.delete(`${PATH}/${uuid}`);
  }

  /**
   * Gets all open worklogs, ordered by day and start time, loading every page of the paged endpoint.
   */
  getOpenWorklogs(): Promise<Array<WorklogsEntry>> {
    return this.getOpenWorklogsFrom(0, []);
  }

  private getOpenWorklogsFrom(page: number, worklogs: Array<WorklogsEntry>): Promise<Array<WorklogsEntry>> {
    return axios.get<SpringPagedResult<WorklogsEntry>>(PATH, {
      params: {
        status: "OPEN",
        page: page,
        size: PAGE_SIZE
      }
    }).then(response => {
      const loaded = worklogs.concat(response.data.content);
      return page + 1 < response.data.page.totalPages
        ? this.getOpenWorklogsFrom(page + 1, loaded)
        : loaded;
    });
  }
}

export default new WorklogServiceV2();
